import type { MrChatSessionView } from '../MrChat/types';

function controllerState(value: unknown): MrChatSessionView['controller'] | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const controller = value as Record<string, unknown>;
  if (
    (controller.state !== 'ready' && controller.state !== 'read_only'
      && controller.state !== 'waiting_for_execution'
      && controller.state !== 'observer_running'
      && controller.state !== 'running'
      && controller.state !== 'interrupted'
      && controller.state !== 'idle_readonly'
      && controller.state !== 'refresh_required')
    || typeof controller.takeover_required !== 'boolean'
  ) return undefined;
  return {
    state: controller.state,
    takeover_required: controller.takeover_required,
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function entryAcknowledgement(value: unknown): string | undefined {
  if (
    typeof value !== 'string'
    || !value.trim()
    || value.length > 180
    || /[\r\n]/.test(value)
  ) return undefined;
  return value.trim();
}

/** Only a typed, server-issued controller envelope may switch Home Chat to MR. */
export function extractMrRouterResponse(value: unknown): MrChatSessionView | null {
  const root = asRecord(value);
  if (!root) return null;
  const workflowChat = asRecord(root.workflow_chat);
  const isNormalizedMrView =
    typeof root.session_id === 'string'
    && (
      'pending_input' in root
      || 'workflow' in root
      || 'current_stage' in root
      || 'completed_stages' in root
    );
  // The API client normalizes the wire envelope before returning it to the
  // panel, which defensively runs this extractor again. Prefer an already
  // normalized controller view so the second pass is idempotent; otherwise a
  // view whose own status is ``mr_chat`` is mistaken for the outer envelope
  // and its Home Chat binding is discarded.
  const candidate = isNormalizedMrView
    ? root
    : asRecord(root.mr_chat)
      ? asRecord(root.mr_chat)
      : root.status === 'workflow_chat' && workflowChat?.workflow_type === 'market_research'
      ? workflowChat
      : null;
  if (!candidate || typeof candidate.session_id !== 'string') return null;
  const homeChatSessionId = typeof root.home_chat_session_id === 'string'
    ? root.home_chat_session_id
    : typeof candidate.home_chat_session_id === 'string' ? candidate.home_chat_session_id : undefined;
  // Keep the controller's optional display notice strictly bounded at the
  // browser seam. It is not a requirement, action, or model instruction.
  const { entry_acknowledgement: rawEntryAcknowledgement, ...rest } = candidate;
  const acknowledgement = entryAcknowledgement(rawEntryAcknowledgement);
  // A continue-in-chat response keeps its controller state at the outer
  // envelope, while normal bound-controller APIs place it on the view. Both
  // are server-issued, intentionally identity-free projections.
  const controller = controllerState(root.controller) || controllerState(candidate.controller);
  return {
    ...(rest as unknown as MrChatSessionView),
    ...(acknowledgement ? { entry_acknowledgement: acknowledgement } : {}),
    ...(controller ? { controller } : {}),
    ...(homeChatSessionId ? { home_chat_session_id: homeChatSessionId } : {}),
  };
}
